import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { CookieService } from 'ngx-cookie';

import { environment } from 'src/environments/environment';
import { TidySession } from 'src/providers/tidy-session/tidy-session';

export interface SocialAuthResult {
  provider: 'google' | 'apple';
  idToken: string;
  accessToken?: string;
  authorizationCode?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
}

export interface SocialAuthResponse {
  access_token: string;
  token_type: string;
  is_new_user: boolean;
  customer_id: number;
  expires_in?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SocialAuthService {
  private initialized = false;
  private closeGoogleButton: (() => void) | null = null;
  private googleCredentialCallback: ((response: any) => void) | null = null;
  private googleIdInitialized = false;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private tidySession: TidySession
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    try {
      await SocialLogin.initialize({
        google: {
          webClientId: environment.google_web_client_id,
          iOSClientId: environment.google_ios_client_id,
          mode: 'online'
        },
        apple: {
          clientId: Capacitor.getPlatform() === 'ios' ? 'com.tidy.clientmobile' : 'com.tidy.web',
          redirectUrl: Capacitor.getPlatform() === 'ios' ? '' : environment.apple_redirect_uri,
          useProperTokenExchange: true
        }
      });
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize social login:', error);
    }
  }

  async signInWithGoogle(): Promise<SocialAuthResult> {
    if (Capacitor.getPlatform() === 'web') {
      return this.signInWithGoogleWeb();
    }
    await this.initialize();
    const result = await SocialLogin.login({
      provider: 'google',
      options: Capacitor.getPlatform() === 'ios' ? {
        scopes: ['email', 'profile']
      } : {}
    });
    if (!result.result) {
      throw new Error('Google sign-in failed');
    }
    const googleResult = result.result;
    if (googleResult.responseType === 'offline') {
      throw new Error('Google sign-in returned offline response, expected online mode');
    }
    return {
      provider: 'google',
      idToken: googleResult.idToken || '',
      accessToken: googleResult.accessToken?.token,
      email: googleResult.profile?.email || undefined,
      firstName: googleResult.profile?.givenName || undefined,
      lastName: googleResult.profile?.familyName || undefined,
      userId: googleResult.profile?.id || undefined
    };
  }

  private signInWithGoogleWeb(): Promise<SocialAuthResult> {
    return new Promise((resolve, reject) => {
      const google = (window as any).google;
      if (typeof google === 'undefined' || !google.accounts?.id) {
        reject(new Error('Google Identity Services not loaded'));
        return;
      }
      const handleCredential = (response: any) => {
        if (!response.credential) {
          reject(new Error('No credential returned'));
          return;
        }
        try {
          const payload = this.parseJwt(response.credential);
          resolve({
            provider: 'google',
            idToken: response.credential,
            accessToken: undefined,
            email: payload.email,
            firstName: payload.given_name,
            lastName: payload.family_name,
            userId: payload.sub
          });
        } catch (error) {
          reject(error);
        }
      };
      // One Tap (google.accounts.id.prompt) is not used: under Chrome's FedCM its status
      // callbacks may never fire, which left the first click doing nothing. Google's
      // rendered button works the same in every browser.
      this.showGoogleButton(handleCredential, reject);
    });
  }

  private showGoogleButton(handleCredential: (response: any) => void, reject: (error: Error) => void): void {
    // A second click while the box is open replaces it instead of stacking another one.
    this.closeGoogleButton?.();
    const google = (window as any).google;
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
    const container = document.createElement('div');
    container.style.cssText = 'background:white;padding:24px;border-radius:12px;text-align:center;';
    const title = document.createElement('p');
    title.textContent = 'Sign in with Google';
    title.style.cssText = 'margin:0 0 16px 0;font-size:16px;font-weight:500;color:#1f1f1f;';
    container.appendChild(title);
    const buttonDiv = document.createElement('div');
    container.appendChild(buttonDiv);
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = 'margin-top:16px;padding:8px 24px;border:1px solid #ccc;border-radius:4px;background:white;cursor:pointer;';
    const close = () => {
      overlay.parentNode?.removeChild(overlay);
      if (this.closeGoogleButton === cancel) {
        this.closeGoogleButton = null;
      }
    };
    const cancel = () => {
      close();
      reject(new Error('Google sign-in was cancelled'));
    };
    cancelBtn.onclick = cancel;
    container.appendChild(cancelBtn);
    overlay.appendChild(container);
    document.body.appendChild(overlay);
    this.closeGoogleButton = cancel;
    this.googleCredentialCallback = (response: any) => {
      close();
      handleCredential(response);
    };
    // GSI warns when initialize() runs more than once, so later boxes only swap the callback.
    if (!this.googleIdInitialized) {
      google.accounts.id.initialize({
        client_id: environment.google_web_client_id,
        callback: (response: any) => this.googleCredentialCallback?.(response),
        auto_select: false
      });
      this.googleIdInitialized = true;
    }
    google.accounts.id.renderButton(buttonDiv, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 280
    });
  }

  private parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => `%${  (`00${  c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
    return JSON.parse(jsonPayload);
  }

  async signInWithApple(): Promise<SocialAuthResult> {
    await this.initialize();
    const result = await SocialLogin.login({
      provider: 'apple',
      options: {
        scopes: ['email', 'name']
      }
    });
    if (!result.result) {
      throw new Error('Apple sign-in failed');
    }
    const appleResult = result.result;
    return {
      provider: 'apple',
      idToken: appleResult.idToken || '',
      accessToken: appleResult.accessToken?.token,
      authorizationCode: appleResult.authorizationCode,
      email: appleResult.profile?.email || undefined,
      firstName: appleResult.profile?.givenName || undefined,
      lastName: appleResult.profile?.familyName || undefined,
      userId: appleResult.profile?.user || undefined
    };
  }

  async authenticateWithBackend(authResult: SocialAuthResult): Promise<SocialAuthResponse> {
    const tidySessionCookie = this.cookieService.get('TidySession');
    const providerClientId = authResult.provider === 'google'
      ? (Capacitor.getPlatform() === 'ios' ? environment.google_ios_client_id : environment.google_web_client_id)
      : (Capacitor.getPlatform() === 'ios' ? 'com.tidy.clientmobile' : 'com.tidy.web');
    const payload: any = {
      provider_name: authResult.provider,
      provider_token: authResult.idToken,
      provider_client_id: providerClientId,
      client_id: environment.client_id_login,
      client_secret: environment.client_secret_login,
      scope: 'customer',
      grant_type: 'password',
      track_data: {
        cookie: tidySessionCookie,
        referrer: this.tidySession.firstUrlViewed?.referrer,
        first_url_viewed: this.tidySession.firstUrlViewed?.value,
        first_url_viewed_at: this.tidySession.firstUrlViewed?.viewedAt,
        onboarding_flow: this.tidySession.onboardingFlow
      }
    };
    if (authResult.provider === 'apple') {
      if (authResult.firstName && authResult.lastName) {
        payload.first_name = authResult.firstName;
        payload.last_name = authResult.lastName;
      }
      if (authResult.authorizationCode) {
        payload.authorization_code = authResult.authorizationCode;
      }
    }
    try {
      const response = await firstValueFrom(
        this.http.post<SocialAuthResponse>(`${environment.api_url}api/v1/token`, payload)
      );
      return response;
    } catch (error) {
      console.error('[GoogleAuth] Backend error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await SocialLogin.logout({ provider: 'google' });
    } catch {
      // Ignore logout errors
    }
    try {
      await SocialLogin.logout({ provider: 'apple' });
    } catch {
      // Ignore logout errors
    }
  }

  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }
}
